import {
  AccessTime,
  History,
  LocationOnOutlined,
  Logout,
  Storefront,
  Tv,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import SoftBox from "components/SoftBox";
import React, { useContext } from "react";
import { UserContext } from "context/user";
import { ConfirmarContext } from "context/ConfirmarContext";
import RecentSalesDialog from "./components/RecentSalesDialog";
import SaleProcessPanel from "./components/SaleProcessPanel";
import SaleSummary from "./components/SaleSummary";
import PaymentFlowDialog from "./components/PaymentFlowDialog";
import { mockEvent } from "./data/ventaEventoMock";
import useVentaEvento from "./hooks/useVentaEvento";
import usePublicadorPantallaCliente from "./hooks/usePublicadorPantallaCliente";
import EventoHeaderInfo from "./components/EventoHeaderInfo";
import EstadoPuntoVenta, { hayEstadoBloqueante } from "./components/EstadoPuntoVenta";
import { ESTADO_IMPRESION, imprimirRanura } from "./impresion";
import { ESTADOS_PANTALLA } from "./pantallaCliente/canal";
import { esVendedorSoloVentaEvento as esSoloVentaEvento } from "utils/accesoRutas";

const RUTA_PANTALLA_CLIENTE = "/venta-evento/pantalla-cliente";

export default function VentaEvento() {
  const sale = useVentaEvento();
  const { user, logout } = useContext(UserContext);
  const { confirmar } = useContext(ConfirmarContext);
  // Vendedor de evento dedicado (sin ningún otro permiso): pantalla completa
  // sin sidenav ni navbar/footer del dashboard, igual que sistema-comedor.
  const esVendedorSoloVentaEvento = !sale.esDemo && esSoloVentaEvento(user);

  const handleLogout = () => {
    confirmar({
      title: "Cerrar sesión",
      message: "¿Estás seguro que querés cerrar sesión?",
      icon: "logout",
      func: logout,
    });
  };
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [recentSalesOpen, setRecentSalesOpen] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  // El diálogo de cobro guarda la venta cerrada para sí; acá sólo hace falta
  // saber que existe para que la pantalla al cliente pase a LISTO.
  const [ventaCerrada, setVentaCerrada] = React.useState(false);

  const selectedPayment = sale.paymentMethods.find((item) => item.code === sale.paymentCode);
  const eventoNombre = sale.esDemo ? mockEvent.name : sale.evento?.nombre;
  const estadoPuntoVenta = {
    loading: sale.loading,
    error: sale.error,
    habilitado: sale.habilitado,
    sinCatalogo: Boolean(sale.habilitado) && sale.products.length === 0,
  };
  const bloqueado = hayEstadoBloqueante(estadoPuntoVenta);

  const estadoPantallaCliente = ventaCerrada
    ? ESTADOS_PANTALLA.LISTO
    : paymentOpen
      ? ESTADOS_PANTALLA.COBRO
      : !bloqueado && (sale.cartItems.length > 0 || sale.customer)
        ? ESTADOS_PANTALLA.VENTA
        : ESTADOS_PANTALLA.STANDBY;

  usePublicadorPantallaCliente({
    estado: estadoPantallaCliente,
    evento: eventoNombre,
    cartItems: sale.cartItems,
    totals: sale.totals,
    customer: sale.customer,
    paymentMethod: selectedPayment,
  });

  const handleCancelSale = () => {
    sale.clearSale();
    setVentaCerrada(false);
    setActiveStep(0);
    setCancelOpen(false);
  };

  const handleNewSale = () => {
    sale.clearSale();
    setVentaCerrada(false);
    setActiveStep(0);
    setPaymentOpen(false);
  };

  const handleCompleteSale = async () => {
    try {
      const venta = await sale.completeSale();
      if (venta) setVentaCerrada(true);
      return venta;
    } catch (error) {
      if (error.requiereReconfirmacion) {
        setPaymentOpen(false);
        setActiveStep(0);
      }
      throw error;
    }
  };

  // Reimpresión desde ventas recientes: el documento se vuelve a pedir a la API
  // para no depender de nada que haya quedado en memoria; este equipo imprime
  // sólo cuando el cajero lo pide.
  const handleReimprimirReciente = async (ventaReciente, ranura) => {
    if (!sale.obtenerDocumentosImpresion) {
      return { estado: ESTADO_IMPRESION.NO_APLICA, error: null };
    }
    try {
      const documentos = await sale.obtenerDocumentosImpresion(ventaReciente.idPedido);
      return await imprimirRanura(documentos, ranura);
    } catch (error) {
      return {
        estado: ESTADO_IMPRESION.ERROR_LOCAL,
        error: error?.message || "No se pudieron recuperar los documentos.",
      };
    }
  };

  const contenido = (
    <>
      <Box sx={{ pb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2.25,
            p: { xs: 2, md: 2.5 },
            borderRadius: "20px",
            color: "#fff",
            background: "linear-gradient(118deg, #20245F 0%, #2C3087 62%, #3E46A4 100%)",
            boxShadow: "0 16px 36px rgba(32, 36, 95, .22)",
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              width: 220,
              height: 220,
              borderRadius: "50%",
              border: "42px solid rgba(103, 193, 184, .18)",
              right: -75,
              top: -112,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, position: "relative", zIndex: 1 }}>
            <Box sx={{ width: 50, height: 50, borderRadius: "15px", display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.12)" }}>
              <Storefront sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Typography component="h1" sx={{ fontSize: { xs: 21, md: 25 }, fontWeight: 900, lineHeight: 1.1 }}>
                  Punto de venta{eventoNombre ? ` · ${eventoNombre}` : ""}
                </Typography>
                {sale.esDemo && (
                  <Chip label="DEMO" size="small" sx={{ height: 22, bgcolor: "#F59B24", color: "#fff", fontWeight: 900, fontSize: 10 }} />
                )}
              </Box>
              {sale.esDemo ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, md: 2 }, mt: 0.75, color: "rgba(255,255,255,.74)" }}>
                  <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12 }}>
                    <LocationOnOutlined sx={{ fontSize: 16 }} /> {mockEvent.pointOfSale}
                  </Typography>
                  <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12 }}>
                    <AccessTime sx={{ fontSize: 16 }} /> {mockEvent.schedule}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>Vendedora: {mockEvent.seller}</Typography>
                </Box>
              ) : (
                <EventoHeaderInfo
                  sede={sale.sede?.nombre}
                  inicio={sale.evento?.inicio}
                  fin={sale.evento?.fin}
                  vendedor={sale.vendedor?.nombre}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, position: "relative", zIndex: 1 }}>
            <Button
              variant="contained"
              startIcon={<History />}
              onClick={() => setRecentSalesOpen(true)}
              data-testid="recent-sales-button"
              sx={{
                bgcolor: "#fff",
                color: "#2C3087",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": { bgcolor: "#F1F5F9" },
              }}
            >
              Ventas recientes
            </Button>
            {/* Enlace común, no window.open: el navegador nunca lo bloquea y la
                pestaña se acomoda a mano en el monitor que mira al comprador. */}
            <Button
              variant="contained"
              component="a"
              href={RUTA_PANTALLA_CLIENTE}
              target="_blank"
              rel="noopener"
              startIcon={<Tv />}
              data-testid="pantalla-cliente-link"
              sx={{
                bgcolor: "rgba(255,255,255,.14)",
                color: "#fff",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": { bgcolor: "rgba(255,255,255,.24)" },
              }}
            >
              Pantalla al cliente
            </Button>
            {esVendedorSoloVentaEvento && (
              <Button
                variant="contained"
                startIcon={<Logout />}
                onClick={handleLogout}
                data-testid="logout-button"
                color="uproPink"
                sx={{
                  borderColor: "rgba(255,255,255,.5)",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Salir
              </Button>
            )}
          </Box>
        </Box>

        {bloqueado ? (
          <EstadoPuntoVenta {...estadoPuntoVenta} onReintentar={sale.refetch} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 360px" },
              alignItems: "start",
              gap: 2.25,
            }}
          >
            <SaleProcessPanel
              activeStep={activeStep}
              products={sale.products}
              cart={sale.cart}
              cartItems={sale.cartItems}
              customer={sale.customer}
              customerCandidates={sale.customerCandidates}
              customerLoading={sale.customerLoading}
              customerError={sale.customerError}
              sampleCustomers={sale.customers}
              avisos={sale.avisos}
              paymentMethods={sale.paymentMethods}
              paymentCode={sale.paymentCode}
              vouchers={sale.vouchers}
              voucherCode={sale.voucherCode}
              observaciones={sale.observaciones}
              totals={sale.totals}
              onStepChange={setActiveStep}
              onAddProduct={sale.addProduct}
              onQuantityChange={sale.changeQuantity}
              onCustomerLookup={sale.identifyCustomer}
              onCustomerSelect={sale.selectCustomerCandidate}
              onCustomerReset={sale.resetCustomer}
              onAnonymousCustomer={sale.useAnonymousConsumer}
              onPaymentChange={sale.setPaymentCode}
              onVoucherChange={sale.selectVoucher}
              onObservacionesChange={sale.setObservaciones}
              onCheckout={() => setPaymentOpen(true)}
            />

            <Box
              sx={{
                position: { lg: "sticky" },
                // Sin DashboardNavbar (kiosco) alcanza con un margen chico; con
                // navbar (sticky, top 12px + 75px de alto) hay que despejarlo
                // para que no tape el resumen al scrollear.
                top: { lg: esVendedorSoloVentaEvento ? 18 : 118 },
              }}
            >
              <SaleSummary
                cartItems={sale.cartItems}
                customer={sale.customer}
                paymentMethod={selectedPayment}
                voucher={sale.voucher}
                observaciones={sale.observaciones}
                totals={sale.totals}
                onEditStep={setActiveStep}
                onClear={() => setCancelOpen(true)}
              />
            </Box>
          </Box>
        )}
      </Box>

      <RecentSalesDialog
        open={recentSalesOpen}
        sales={sale.recentSales}
        esDemo={sale.esDemo}
        onClose={() => setRecentSalesOpen(false)}
        onReimprimir={sale.esDemo ? undefined : handleReimprimirReciente}
      />
      <PaymentFlowDialog
        open={paymentOpen}
        cartItems={sale.cartItems}
        customer={sale.customer}
        paymentMethod={selectedPayment}
        voucher={sale.voucher}
        totals={sale.totals}
        onClose={() => setPaymentOpen(false)}
        onComplete={handleCompleteSale}
        onNewSale={handleNewSale}
        onObtenerDocumentos={sale.obtenerDocumentosImpresion}
        esDemo={sale.esDemo}
      />

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>¿Cancelar la venta actual?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#64748B", fontSize: 14 }}>
            Se quitarán productos, voucher, identificación y cobro seleccionados. Esta acción solamente afecta la demo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCancelOpen(false)}
            sx={{ color: "#2C3087", textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: "#F3F2FF" } }}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelSale}
            sx={{
              bgcolor: "#C62828",
              color: "#fff",
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: "#A91F1F" },
            }}
          >
            Cancelar venta
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (esVendedorSoloVentaEvento) {
    return <SoftBox sx={{ minHeight: "100vh", p: { xs: 2, md: 3 } }}>{contenido}</SoftBox>;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {contenido}
      <Footer />
    </DashboardLayout>
  );
}
